import React, { useEffect } from "react";
import FormSubmitButton from "Common/Custom/FormSubmitButton";
import PageBody from "Common/Custom/PageBody";
import { HeadingOne } from "Common/Custom/HeadingOne";
import FormRenderer from "Common/Custom/FormRenderer";

/**
 * AddSalaryGenerateForm
 * 
 * Following the EXACT "Code Flow" of SalaryType/AddSalaryTypeForm.jsx:
 * 1. Uses FormRenderer to map JSON fields to UI.
 * 2. Standardized props and PageBody structure.
 */
const AddSalaryGenerateForm = ({
    fields = {},
    values = {},
    handleChange = () => { },
    handleSubmit = () => { },
    loading = false,
    errors = {},
    title = "Add Salary Generate"
}) => {

    const options = [
        {
            label: "Monthly",
            value: "monthly"
        },
        {
            label: "Weekly",
            value: "weekly"
        },
        {
            label: "Bi-Weekly",
            value: "BIWEEKLY"
        }
    ]

    const statusOptions = [
        {
            label: "generated",
            value: "generated"
        },
        {
            label: "approved",
            value: "approved"
        },
        {
            label: "paid",
            value: "paid"
        },
        {
            label: "cancelled",
            value: "cancelled"
        }
    ]

    const optionConfig = {
        pay_frequency: {
            list: options,
            valueKey: "value",
            labelKey: "label"
        },
        status: {
            list: statusOptions,
            valueKey: "value",
            labelKey: "label"
        }
    };

    const currentDate = new Date();
    const firstDayOfPrevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    const lastDayOfCurrentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const minDate = formatDate(firstDayOfPrevMonth);
    const maxDate = formatDate(lastDayOfCurrentMonth);

    useEffect(() => {
        if (values.from && values.pay_frequency) {
            const calculatedDateObj = new Date(values.from);

            if (values.pay_frequency === "monthly") {
                calculatedDateObj.setMonth(calculatedDateObj.getMonth() + 1);
                calculatedDateObj.setDate(calculatedDateObj.getDate() - 1); // 1 month period
            } else if (values.pay_frequency === "weekly") {
                calculatedDateObj.setDate(calculatedDateObj.getDate() + 6); // 1 week period
            } else if (values.pay_frequency === "BIWEEKLY") {
                calculatedDateObj.setDate(calculatedDateObj.getDate() + 13); // 2 weeks period
            }

            const calculatedToDate = formatDate(calculatedDateObj);

            if (values.to !== calculatedToDate) {
                // Programmatically trigger handleChange to update main state
                handleChange({
                    target: {
                        name: "to",
                        value: calculatedToDate
                    }
                });
            }
        }
    }, [values.from, values.pay_frequency, values.to, handleChange]);

    const updatedFields = (fields.formdata || []).map((field) => {
        if (field.name === "from") {
            return {
                ...field,
                minDate: minDate,
                maxDate: values.to || maxDate
            };
        }
        if (field.name === "to") {
            return {
                ...field,
                disabled: true, // Make it completely read-only
                readOnly: true
            };
        }
        if (field.name === "status") {
            return {
                ...field,
                disabled: true, // Make it completely read-only
                readOnly: true
            };
        }
        return field;
    });

    return (
        <PageBody
            card={true}
            onSubmit={handleSubmit}
            headerchildren={<HeadingOne>{title}</HeadingOne>}
        >
            {/* Dynamic rendering from JSON configuration (SalaryType Flow) */}
            <FormRenderer
                fields={updatedFields}
                values={values}
                errors={errors}
                handleChange={handleChange}
                optionConfig={optionConfig}
            />

            <div className="mt-4">
                <FormSubmitButton
                    label="Generate Bulk Salary"
                    loading={loading}
                    colSize="auto"
                />
            </div>

            <div className="alert alert-info border-0 shadow-sm rounded-3 mt-4">
                <div className="d-flex align-items-center gap-2">
                    <i className="bi bi-info-circle-fill"></i>
                    <div className="small">
                        <strong>Bulk Generation:</strong> This will create salary sheets for ALL active employees based on their current Salary Setup.
                    </div>
                </div>
            </div>
        </PageBody>
    );
};

export default AddSalaryGenerateForm;
